/* One ordinary, removable parcel-protection line per cart. */
(() => {
  const node = document.querySelector('[data-lp-parcel-protection]');
  if (!node || window.LPParcelProtection) return;
  const config = JSON.parse(node.textContent);
  if (!config.enabled || !config.variant || !config.available) return;
  const protectionId = String(config.variant);
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const declinedKey = '_lp_parcel_declined';
  let queue = Promise.resolve();
  const isProtection = item => String(item.variant_id || item.id) === protectionId;
  const hasMerchandise = cart => cart.items.some(item => !isProtection(item));
  const jsonResponse = (data, original) => {
    const headers = new Headers(original.headers);
    headers.set('Content-Type', 'application/json');
    headers.delete('Content-Length');
    headers.delete('Content-Encoding');
    return new Response(JSON.stringify(data), {status: original.status, statusText: original.statusText, headers});
  };
  async function readCart() {
    const response = await nativeFetch(root + 'cart.js', {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
    return cart;
  }
  async function decode(request) {
    const type = request.headers.get('Content-Type') || '';
    if (type.includes('application/json')) return {json: await request.clone().json()};
    if (type.includes('multipart/form-data') || type.includes('application/x-www-form-urlencoded')) return {form: await request.clone().formData()};
    return null;
  }
  function addProtection(request, body) {
    const headers = new Headers(request.headers);
    let single = false, primaryId = '', outgoing;
    if (body.json) {
      const data = {...body.json};
      let items;
      if (Array.isArray(data.items)) items = data.items.map(item => ({...item}));
      else if (data.id) {
        single = true;
        const item = {id: data.id, quantity: data.quantity || 1};
        for (const key of ['properties', 'selling_plan']) if (data[key] != null) item[key] = data[key];
        items = [item];
        for (const key of ['id', 'quantity', 'properties', 'selling_plan']) delete data[key];
      } else return null;
      primaryId = String(items[0]?.id || '');
      if (items.some(item => String(item.id) === protectionId)) return null;
      data.items = [...items, {id: protectionId, quantity: 1}];
      outgoing = JSON.stringify(data);
    } else {
      const original = body.form, form = new FormData();
      single = original.has('id');
      primaryId = String(original.get('id') || original.get('items[0][id]') || '');
      let last = -1;
      for (const [key, value] of original.entries()) {
        if ((key === 'id' || /^items\[\d+\]\[id\]$/.test(key)) && String(value) === protectionId) return null;
        const match = key.match(/^items\[(\d+)\]/);
        if (match) last = Math.max(last, Number(match[1]));
        let target = key;
        if (single) {
          if (['id', 'quantity', 'selling_plan'].includes(key)) target = 'items[0][' + key + ']';
          else if (key.startsWith('properties[')) target = 'items[0][properties]' + key.slice(10);
        }
        form.append(target, value);
      }
      if (!single && last < 0) return null;
      const index = single ? 1 : last + 1;
      form.append('items[' + index + '][id]', protectionId);
      form.append('items[' + index + '][quantity]', '1');
      headers.delete('Content-Type');
      outgoing = form;
    }
    return {request: new Request(request, {body: outgoing, headers}), single, primaryId};
  }
  async function updateCart(data) {
    const response = await nativeFetch(root + 'cart/update.js', {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(data)});
    return {response, cart: response.ok ? await response.clone().json() : null};
  }
  async function mutate(request, action) {
    let before, body;
    try { [before, body] = await Promise.all([readCart(), decode(request)]); }
    catch {
      return new Response(JSON.stringify({status: 503, description: 'Your cart could not be checked. Please try again.'}), {status: 503, headers: {'Content-Type': 'application/json'}});
    }
    if (!body) return nativeFetch(request);
    let modified = null;
    if (action === 'add' && !before.items.some(isProtection) && (!before.items.length || before.attributes?.[declinedKey] !== '1')) {
      modified = addProtection(request, body);
    }
    const response = await nativeFetch(modified?.request || request);
    if (!response.ok) return response;
    let result;
    try { result = await response.clone().json(); } catch { return response; }
    if (result.status >= 400) return response;
    if (action === 'add') {
      if (modified?.single && Array.isArray(result.items)) {
        const primary = result.items.find(item => String(item.variant_id || item.id) === modified.primaryId);
        if (primary) return jsonResponse({...primary, ...result}, response);
      }
      return response;
    }
    if (!Array.isArray(result.items)) return response;
    if (before.items.some(isProtection) && !result.items.some(isProtection) && hasMerchandise(result)) {
      // Remember a customer's removal in this cart, across pages and tabs.
      await updateCart({attributes: {[declinedKey]: '1'}});
    } else if (hasMerchandise(before) && !hasMerchandise(result) && result.items.some(isProtection)) {
      // Never leave a default protection-only basket after the last product is removed.
      const source = body.json || Object.fromEntries(body.form.entries());
      const updates = Object.fromEntries(result.items.filter(isProtection).map(item => [item.key, 0]));
      const data = {updates, attributes: {[declinedKey]: ''}};
      for (const key of ['sections', 'sections_url']) if (source[key]) data[key] = source[key];
      const cleaned = await updateCart(data);
      if (cleaned.cart) return cleaned.response;
    } else if (!result.items.length && result.attributes?.[declinedKey]) {
      await updateCart({attributes: {[declinedKey]: ''}});
    }
    return response;
  }
  window.fetch = function(input, init) {
    const url = new URL(input instanceof Request ? input.url : input, location.href);
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/);
    if (url.origin !== location.origin || method !== 'POST' || !match) return nativeFetch(input, init);
    const effective = input instanceof Request ? new Request(input, init) : new Request(url, init);
    const run = () => navigator.locks?.request ? navigator.locks.request('lp-parcel-cart', () => mutate(effective, match[1])) : mutate(effective, match[1]);
    const operation = queue.then(run, run);
    queue = operation.catch(() => {});
    return operation;
  };
  window.LPParcelProtection = {enabled: true, variant: protectionId};
})();
